import { useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, ZoomIn, ImageOff } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { BACKEND_URL } from "@/lib/api";

function ImgWithFallback({ src, alt, className, iconClass }) {
    const [errored, setErrored] = useState(false);

    const handleError = useCallback(() => setErrored(true), []);

    if (errored || !src) {
        return (
            <div className={`${className} flex items-center justify-center bg-gradient-to-br from-brand-light/60 to-white`}>
                <ImageOff className={iconClass || "w-10 h-10 text-white"} />
            </div>
        );
    }

    return (
        <img
            src={src}
            alt={alt}
            className={className}
            onError={handleError}
            loading="lazy"
        />
    );
}

export default function Gallery({ cover, images = [] }) {
    const all = [cover, ...images.filter((u) => u && u !== cover)].filter(Boolean);
    const [active, setActive] = useState(0);
    const [zoom, setZoom] = useState(false);

    const url = (u) => u?.startsWith("/") ? `${BACKEND_URL}${u}` : u;
    const total = all.length;
    const next = useCallback(() => setActive((i) => (i + 1) % total), [total]);
    const prev = useCallback(() => setActive((i) => (i - 1 + total) % total), [total]);

    if (total === 0) return null;

    return (
        <div data-testid="book-gallery" className="w-full">
            <div className="relative w-full max-w-[280px] sm:max-w-sm md:max-w-md mx-auto aspect-[3/4] rounded-2xl sm:rounded-3xl shadow-pop overflow-hidden bg-gradient-to-br from-brand-light/60 to-white group">
                <ImgWithFallback
                    src={url(all[active])}
                    alt="cover"
                    className="w-full h-full object-cover transition-opacity duration-500"
                    iconClass="w-12 h-12 sm:w-16 sm:h-16 text-brand-navy/30"
                />
                {total > 1 && (
                    <>
                        <button onClick={prev} className="absolute left-1 sm:left-2 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/85 hover:bg-white grid place-items-center shadow-soft opacity-0 group-hover:opacity-100 transition" data-testid="gallery-prev"><ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-brand-navy" /></button>
                        <button onClick={next} className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/85 hover:bg-white grid place-items-center shadow-soft opacity-0 group-hover:opacity-100 transition" data-testid="gallery-next"><ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-brand-navy" /></button>
                    </>
                )}
                <button onClick={() => setZoom(true)} className="absolute top-2 right-2 sm:top-3 sm:right-3 w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-white/85 hover:bg-white grid place-items-center shadow-soft" data-testid="gallery-zoom">
                    <ZoomIn className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-brand-navy" />
                </button>
                {total > 1 && (
                    <div className="absolute bottom-2 sm:bottom-3 left-1/2 -translate-x-1/2 flex gap-1 px-1.5 sm:px-2 py-1 bg-white/85 rounded-full">
                        {all.map((_, i) => <span key={i} className={`h-1 sm:h-1.5 rounded-full transition-all ${i === active ? "w-4 sm:w-6 bg-brand-royal" : "w-1 sm:w-1.5 bg-brand-royal/30"}`} />)}
                    </div>
                )}
            </div>

            {/* Thumbnails slider */}
            {total > 1 && (
                <div className="mt-3 sm:mt-4 w-full">
                    <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-2 scroll-smooth snap-x justify-center">
                        {all.map((u, i) => (
                            <button key={i} onClick={() => setActive(i)} className={`flex-shrink-0 w-12 h-16 sm:w-16 sm:h-20 rounded-lg overflow-hidden border-2 snap-start transition ${i === active ? "border-brand-royal ring-2 ring-brand-royal/30" : "border-brand-light hover:border-brand-royal/40"}`} data-testid={`gallery-thumb-${i}`}>
                                <ImgWithFallback src={url(u)} alt="" className="w-full h-full object-cover" iconClass="w-5 h-5 text-brand-navy/30" />
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <Dialog open={zoom} onOpenChange={setZoom}>
                <DialogContent className="max-w-4xl w-[95vw] sm:w-full p-1 sm:p-2 bg-black/95 border-0 z-[100] [&_.lucide-x]:text-white">
                    <ImgWithFallback
                        src={url(all[active])}
                        alt="full"
                        className="w-full max-h-[70vh] sm:max-h-[80vh] object-contain"
                        iconClass="w-16 h-16 sm:w-20 sm:h-20 text-white/40"
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
}
